import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/features/auth/api';
import { brandProfileSchema, industries, type BrandProfileInput } from '@/lib/schemas/brandKit';
import { useAuthStore } from '@/lib/auth/authStore';
import { useCurrentCompany } from '@/lib/branding/useCurrentBrand';
import { OnboardingShell } from '@/components/layout/OnboardingShell';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { FormField } from '@/components/forms/FormField';
import { toast } from '@/components/ui/Toast';
import { queryKeys } from '@/lib/api/queryKeys';
import { useT } from '@/lib/i18n/useT';

export function BrandProfilePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const patchUser = useAuthStore((s) => s.patchUser);
  const t = useT();
  const { company } = useCurrentCompany();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<BrandProfileInput>({ resolver: zodResolver(brandProfileSchema) });

  // Pre-fill the company name from the company created during registration so
  // the user doesn't retype it. keepDirtyValues preserves anything already
  // edited if the company query resolves after the form rendered.
  useEffect(() => {
    if (company?.name) {
      reset({ companyName: company.name }, { keepDirtyValues: true });
    }
  }, [company?.name, reset]);

  const save = useMutation({
    mutationFn: (input: BrandProfileInput) => authApi.completeProfile(input),
    onSuccess: ({ user }) => {
      patchUser({ ...user });
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me });
      queryClient.invalidateQueries({ queryKey: queryKeys.plans.current });
      toast({
        tone: 'success',
        title: t('onboarding.profile.savedTitle'),
        description: t('onboarding.profile.savedBody'),
      });
      navigate('/onboarding/brandkit', { replace: true });
    },
    onError: (e) =>
      toast({
        tone: 'error',
        title: t('onboarding.profile.errorTitle'),
        description: (e as Error).message,
      }),
  });

  return (
    <OnboardingShell
      step={2}
      title={t('onboarding.profile.title')}
      subtitle={t('onboarding.profile.subtitle')}
    >
      <form className="grid gap-5" onSubmit={handleSubmit((v) => save.mutate(v))}>
        <FormField
          label={t('onboarding.profile.companyName')}
          required
          error={errors.companyName?.message}
        >
          <Input
            placeholder={t('onboarding.profile.companyNamePlaceholder')}
            {...register('companyName')}
          />
        </FormField>
        <FormField
          label={t('onboarding.profile.yourName')}
          required
          error={errors.contactName?.message}
        >
          <Input
            placeholder={t('onboarding.profile.yourNamePlaceholder')}
            {...register('contactName')}
          />
        </FormField>
        <FormField
          label={t('onboarding.profile.industry')}
          required
          error={errors.industry?.message}
        >
          <Select defaultValue="" {...register('industry')}>
            <option value="" disabled>
              {t('onboarding.profile.pickOne')}
            </option>
            {industries.map((i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </Select>
        </FormField>
        <div className="flex justify-end pt-2">
          <Button type="submit" loading={save.isPending} size="lg">
            {t('onboarding.profile.continue')}
          </Button>
        </div>
      </form>
    </OnboardingShell>
  );
}
